import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { Dialog, SharedModule, DataService } from '@vendure/admin-ui/core';
import { Subject, debounceTime, distinctUntilChanged, takeUntil } from 'rxjs';
import { gql } from 'graphql-tag';

const CHECK_IDENTIFIER_AVAILABILITY = gql`
    query CheckStoreSiteIdentifierAvailability($identifier: String!) {
        checkStoreSiteIdentifierAvailability(identifier: $identifier)
    }
`;

export interface CreateStoreSiteDialogResult {
    name: string;
    identifier?: string;
    isDemo?: boolean;
    demoExpiresAt?: string;
    demoCustomerName?: string;
    demoCustomerEmail?: string;
    /**
     * When duplicating from an existing StoreSite, this is the source StoreSite id.
     */
    sourceStoreSiteId?: string;
    /**
     * Optional flags for how the new StoreSite should be created.
     */
    options?: {
        duplicatePages?: boolean;
    };
}

@Component({
    selector: 'vdr-create-store-site-dialog',
    templateUrl: './create-store-site-dialog.component.html',
    styleUrls: ['./create-store-site-dialog.component.scss'],
    standalone: true,
    imports: [SharedModule, ReactiveFormsModule],
})
export class CreateStoreSiteDialogComponent implements Dialog<CreateStoreSiteDialogResult>, OnInit, OnDestroy {
    resolveWith: (result?: CreateStoreSiteDialogResult) => void;

    /**
     * When provided via ModalService.locals, the dialog switches to "duplicate" mode.
     * Example:
     * modalService.fromComponent(CreateStoreSiteDialogComponent, {
     *   locals: { existingStoreSite },
     *   size: 'lg',
     *   closable: true,
     * });
     */
    existingStoreSite: any | undefined;
    userIsSuperAdmin = false;
    forceDemoMode = false;

    constructor(private dataService: DataService) {}

    nameControl = new FormControl('', [
        Validators.required,
        Validators.minLength(3),
    ]);

    identifierControl = new FormControl('', [
        Validators.required,
        Validators.pattern(/^[a-z0-9-]{1,64}$/),
    ]);

    demoAccountControl = new FormControl(false);
    demoCustomerNameControl = new FormControl('');
    demoCustomerEmailControl = new FormControl('', [Validators.email]);
    demoExpiresAtControl = new FormControl('');

    /**
     * Only relevant when duplicating an existing StoreSite.
     */
    duplicatePagesControl = new FormControl(true);

    isVerifyingIdentifier = false;
    identifierAvailable: boolean | null = null;

    private destroy$ = new Subject<void>();

    ngOnInit() {
        // If we are in "duplicate" mode, we can pre-fill the name field to help the user.
        if (this.existingStoreSite?.name && !this.nameControl.value) {
            this.nameControl.setValue(`${this.existingStoreSite.name} Copy`);
        }

        // Watch for identifier changes to validate availability
        this.identifierControl.valueChanges.pipe(
            debounceTime(500),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(value => {
            this.validateIdentifierAvailability(value);
        });

        this.demoAccountControl.valueChanges.pipe(
            takeUntil(this.destroy$),
        ).subscribe(isDemo => {
            this.applyIdentifierValidationForMode(!!isDemo);
            if (isDemo) {
                this.identifierControl.setValue('');
                this.identifierAvailable = null;
            }
        });

        if (this.forceDemoMode) {
            this.demoAccountControl.setValue(true);
            this.demoAccountControl.disable();
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    onStoreNameChange() {
        if (this.demoAccountControl.value) {
            return;
        }
        const storeName = this.nameControl.value || '';
        // Auto-generate identifier from store name
        const autoIdentifier = storeName.toLowerCase()
            .replace(/[^a-z0-9-]/g, '-')
            .replace(/-+/g, '-')
            .replace(/^-|-$/g, '')
            .substring(0, 64);

        this.identifierControl.setValue(autoIdentifier);
    }

    validateIdentifierAvailability(value: string | null) {
        if (this.demoAccountControl.value || !value || this.identifierControl.invalid) {
            this.isVerifyingIdentifier = false;
            this.identifierAvailable = null;
            return;
        }

        // Start verification
        this.isVerifyingIdentifier = true;
        this.identifierAvailable = null;

        // Make API call to check availability
        this.dataService.query(CHECK_IDENTIFIER_AVAILABILITY, { identifier: value })
            .single$
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (result: any) => {
                    this.isVerifyingIdentifier = false;
                    this.identifierAvailable = result.checkStoreSiteIdentifierAvailability;
                },
                error: () => {
                    this.isVerifyingIdentifier = false;
                    this.identifierAvailable = null;
                }
            });
    }

    isFormValid(): boolean {
        const isDemo = !!this.demoAccountControl.value;
        const hasDemoFields =
            !isDemo ||
            !!this.demoExpiresAtControl.value &&
            !!this.demoCustomerNameControl.value?.trim() &&
            !!this.demoCustomerEmailControl.value?.trim() &&
            this.demoCustomerEmailControl.valid;

        const hasIdentifier = !isDemo
            ? this.identifierControl.valid &&
              !this.isVerifyingIdentifier &&
              this.identifierAvailable === true &&
              !!this.identifierControl.value?.trim()
            : true;

        return this.nameControl.valid &&
            !!this.nameControl.value?.trim() &&
            hasIdentifier &&
            hasDemoFields;
    }

    get isDuplicateMode(): boolean {
        return !!this.existingStoreSite;
    }

    cancel() {
        this.resolveWith();
    }

    submit() {
        if (this.isFormValid()) {
            const isDemo = !!this.demoAccountControl.value;
            const result: CreateStoreSiteDialogResult = {
                name: this.nameControl.value!.trim(),
                isDemo,
            };
            if (!isDemo) {
                result.identifier = this.identifierControl.value!.trim();
            }

            if (isDemo) {
                result.demoExpiresAt = this.demoExpiresAtControl.value!;
                result.demoCustomerName = this.demoCustomerNameControl.value!.trim();
                result.demoCustomerEmail = this.demoCustomerEmailControl.value!.trim();
            }

            if (this.existingStoreSite?.id) {
                result.sourceStoreSiteId = this.existingStoreSite.id;
                result.options = {
                    duplicatePages: !!this.duplicatePagesControl.value,
                };
            }

            this.resolveWith(result);
        }
    }

    private applyIdentifierValidationForMode(isDemo: boolean): void {
        if (isDemo) {
            this.identifierControl.clearValidators();
        } else {
            this.identifierControl.setValidators([
                Validators.required,
                Validators.pattern(/^[a-z0-9-]{1,64}$/),
            ]);
        }
        this.identifierControl.updateValueAndValidity();
    }
}
